/**
 * The licence status card: the ACTIVE header, the six-cell detail grid, and the
 * four primary actions.
 *
 * "Active", "Commercial Creator" and "Lifetime" are literals here exactly as in the
 * prototype. They are correct for the only licence the app sells; once the backend
 * serves a License record, they become fields on it (see docs/api-contract.md).
 */
import { Copy, Crown, Download, FileText, Printer, ScanLine, ShieldCheck } from 'lucide-react';
import { Btn } from '../ui';
import { c } from '../../styles/theme';

/** One cell in the status card's detail grid. */
function DetailCell({
  label,
  value,
  onCopy,
}: {
  label: string;
  value: string;
  onCopy?: () => void;
}) {
  return (
    <div
      style={{
        border: `1px solid ${c.border}`,
        borderRadius: 8,
        padding: '8px 10px',
        background: c.surface2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 6,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <p style={{ fontSize: 10, color: c.text3, margin: '0 0 2px', textTransform: 'uppercase' }}>
          {label}
        </p>
        <p
          style={{
            fontSize: 12,
            fontWeight: 500,
            margin: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {value}
        </p>
      </div>
      {onCopy && (
        <button
          type="button"
          onClick={onCopy}
          style={{ background: 'none', border: 'none', cursor: 'pointer', flexShrink: 0 }}
          aria-label={`Copy ${label.toLowerCase()}`}
        >
          <Copy size={12} color={c.text3} />
        </button>
      )}
    </div>
  );
}

export interface LicenseStatusCardProps {
  licenseNumber: string;
  /** Human-formatted issue date, or "—". */
  issued: string;
  /** Agreement version, without the "v". */
  version: string;
  /** Public-facing lifecycle state. */
  status?: 'active' | 'revoked' | 'suspended' | 'refunded' | 'expired';
  onCopyNumber: () => void;
  onDownload: () => void;
  onVerify: () => void;
  onViewAgreement: () => void;
  onPrint: () => void;
}

function statusLabel(status: LicenseStatusCardProps['status']): string {
  switch (status) {
    case 'revoked':
      return 'REVOKED';
    case 'suspended':
      return 'SUSPENDED';
    case 'refunded':
      return 'REFUNDED';
    case 'expired':
      return 'EXPIRED';
    case 'active':
    default:
      return 'ACTIVE';
  }
}

function statusDescription(status: LicenseStatusCardProps['status']): string {
  switch (status) {
    case 'revoked':
      return 'This commercial licence was revoked. Contact Lyrixsa AI support if you need the record reviewed.';
    case 'suspended':
      return 'This commercial licence is temporarily suspended pending review.';
    case 'refunded':
      return 'This commercial licence was refunded and is no longer active.';
    case 'expired':
      return 'This commercial licence has expired.';
    case 'active':
    default:
      return 'You have full commercial rights to use, sell, and distribute music created with Lyrixsa AI.';
  }
}

export default function LicenseStatusCard({
  licenseNumber,
  issued,
  version,
  status = 'active',
  onCopyNumber,
  onDownload,
  onVerify,
  onViewAgreement,
  onPrint,
}: LicenseStatusCardProps) {
  return (
    <div
      style={{
        background: c.surface1,
        border: `1px solid ${c.accentBorder}`,
        borderRadius: 12,
        padding: '18px 20px',
        marginBottom: 18,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 16 }}>
        <div
          style={{
            width: 46,
            height: 46,
            borderRadius: 12,
            background: c.accentSoft,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            position: 'relative',
          }}
        >
          <ShieldCheck size={22} color={c.accent} />
          <Crown size={12} color={c.accent} style={{ position: 'absolute', top: -4, right: -4 }} />
        </div>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <h1
              style={{
                fontFamily: 'Space Grotesk',
                fontWeight: 600,
                fontSize: 16,
                margin: 0,
                color: c.text1,
              }}
            >
              Commercial Creator License
            </h1>
            <span
              style={{
                fontSize: 10,
                color: status === 'active' ? c.success : c.text2,
                background: status === 'active' ? c.successSoft : c.surface2,
                padding: '2px 8px',
                borderRadius: 999,
                fontWeight: 600,
              }}
            >
              {statusLabel(status)}
            </span>
          </div>
          <p style={{ fontSize: 12, color: c.text2, margin: '3px 0 0', lineHeight: 1.5 }}>
            {statusDescription(status)}
          </p>
        </div>
      </div>

      <div className="lyx-grid-2" style={{ marginBottom: 16 }}>
        <DetailCell label="License number" value={licenseNumber} onCopy={onCopyNumber} />
        <DetailCell label="License type" value="Commercial Creator" />
        <DetailCell label="Status" value={statusLabel(status)} />
        <DetailCell label="Purchase date" value={issued} />
        <DetailCell label="License version" value={`v${version}`} />
        <DetailCell label="Expires" value="Lifetime" />
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Btn onClick={onDownload}>
          <Download size={13} />
          Download Certificate
        </Btn>
        <Btn variant="secondary" onClick={onVerify}>
          <ScanLine size={13} />
          Verify License
        </Btn>
        <Btn variant="secondary" onClick={onViewAgreement}>
          <FileText size={13} />
          View License Agreement
        </Btn>
        <Btn variant="secondary" onClick={onPrint}>
          <Printer size={13} />
          Print
        </Btn>
      </div>
    </div>
  );
}
